import Backbone from 'backbone'
import $ from 'jquery'
import filterText from '../filters/filter-text'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import nonAbbreviatedValuesTooltip from '../non-abbreviated-values-tooltip.hbs'
import deviceType from 'scoreboard/components/device-type'
import FilterDialog from '../filters/filter-dialog'
import datasetsResultHeader from './datasets-result-header.hbs'
import permissions from 'scoreboard/components/permissions'
import unwrapModel from 'scoreboard/components/unwrap-model'
import { shouldShowCreateDatasetMetricTooltip } from 'scoreboard/datasets/overview/cell-tooltip'

/**
 * Shows information about a field in a dataset.  Intended as a superclass.
 *
 * @class DatasetsResult
 */
var DatasetsResult = Backbone.View.extend({

	className: 'panel panel-with-table has-sticky-headers',

	events: {
		'click .back': 'back',
		'click .object-filter': 'showFilters',
		'click .icon-settings': 'settings',
		'click .icon-delete': 'delete',
		'click tbody tr:not([data-id=other]) td .value': 'clickValueCell'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the Dataset
	 *
	 * @param {Object} options.field the field this result is based on
	 *
	 * @param {Array} options.allFilters all the filters that currently apply
	 * to the Datasets Overview
	 *
	 * @param {Object} options.result the result of the filter
	 *
	 * @param {Object} options.treeItemsById a hash of tree IDs to tree items
	 *
	 * @param {Object} options.filtersByLinkGroup a hash of filters by linkGroup
	 *
	 * @param {Boolean} options.explore true if we're on the datasets explore
	 * tab. In that case, the clickable dataset values should be highlighted.
	 *
	 * @param {DatasetTable} options.table
	 */
	initialize: function(options) {
		this.options = options || {}
		this.$el.attr('data-field-id', _.get(options, 'field.id'))
		this.$el.attr('data-table-cid', this.options.table.cid)
		this.setAllFilters(this.options.allFilters)
	},

	setAllFilters: function(allFilters) {
		this.allFilters = allFilters
		this.filters = _.filter(allFilters, f => {
			return f.fieldId == this.options.field.id && _.isNil(f.aggregationId)
		})
	},

	renderHeader: function() {
		this.$('.panel-header').remove()
		this.$el.prepend(datasetsResultHeader(this.headerContext()))
	},

	headerContext: function() {
		let tableFilters = headerContextFilters(this.filters)

		let context = {
			heading: this.headerLabel(),
			multipleFilters: _.size(tableFilters) > 1,
			numFilters: _.size(tableFilters),
			valid: _.result(this.options.filter, 'valid', true)
		}
		// If the dataset ID is different from the model id, the field is linked dataset
		let fieldDatasetId = _.get(this.options, 'field.datasetId')
		if (!_.isNil(fieldDatasetId) &&
			fieldDatasetId !== this.model.get('rootDatasetId')) {
			context.linkedDataset = this.model.datasetsById[fieldDatasetId].get('label')
		}

		let filterContext = headerFilterContext({
			filters: tableFilters,
			dataset: this.model,
			underlyingField: this.options.field,
			treeItemsById: this.options.treeItemsById,
			filtersByLinkGroup: this.options.filtersByLinkGroup,
			parentId: this.options.result.parentId
		})

		this.hasFilterText = filterContext.hasFilterText
		_.extend(context, filterContext)

		return context
	},

	updateHeaderLabel: function () {
		let label = this.headerLabel()

		this.$el.toggleClass('has-header-label', !!label)
		this.$el.toggleClass('has-filter-text', this.hasFilterText)
		this.$('h3').text(label)
	},

	headerLabel: function () {
		return this.model.getFieldLabelByIds(this.options.result)
	},

	showFilters: function() {
		let filterDialog = new FilterDialog(_.extend({
			// we want to support relative dates on the Datasets Explore tab to
			// support dynamic columns like SA per 1000 from #27466
			allowAppCalendar: true,
			panelHeading: this.options.field.label
		}, this.options))
		appDisplay.dialog(filterDialog)
		filterDialog.on('done', (newFilters) =>
			this.trigger('change-filters', this.filters, newFilters))
	},

	settings: function() {
		this.trigger('settings', this.options)
	},

	'delete': function(e) {
		var index = $(e.target).closest('.panel').index()
		this.trigger('delete', index)
	},

	'back': function(e) {
		this.trigger('back', this.options.table, this.options.result)
	},

	/**
	 * Returns the options to be passed to the dataset-series-creator form
	 */
	getDatasetSeriesOptions: function(e) {
		const options = _.pick(this.options.request, 'id', 'aggregationType',
			'aggregationFieldId', 'aggregationVisibleLinkedFieldId')
		options.filters = this.allFilters
		options.model = this.model
		let calendarFieldIdentifier = this.model.getCalendarFieldIdentifier()
		if (!_.isNil(calendarFieldIdentifier)) {
			options.calendarFieldIdentifier = calendarFieldIdentifier
			let calendarIds = this.model.fieldIdentifierToIds(calendarFieldIdentifier)
			options.calendarFieldId = calendarIds.fieldId
			options.calendarVisibleLinkedFieldId = calendarIds.visibleLinkedFieldId
		}
		return options
	},

	/**
	 * Callback after a user clicks on a value in the table of results.
	 * In the datasets section, this triggers a tooltip with a single option:
	 * Create Dataset Metric. In dashboards it allows drilling down on the
	 * value (#33313)
	 * Setup non-abbreviated tooltips for values in the results tables
	 */
	clickValueCell: function(e) {
		this.trigger('click-value-cell', e.target, this.getDatasetSeriesOptions(e))
	},

	/**
	 * Setup non-abbreviated tooltips for values in the results tables
	 */
	setupNonAbbreviatedTooltips: function() {
		this.nonAbbreviatedTooltip = new TooltipAndDetails({
			el: this.el,
			selector: '.is-shortened',
			tooltipContent: (e) => {
				const showCreateDatasetMetric = deviceType.touchOnly &&
					shouldShowCreateDatasetMetricTooltip(e.target)
				return nonAbbreviatedValuesTooltip({
					header: e.target.dataset.field,
					value: e.target.dataset.unshortenedValue,
					showCreateDatasetMetric
				})
			},
			tooltipOptions: {
				// In non-mobile, close this tooltip on el click because the 'Create Dataset Metric' shows
				// up then. On mobile, keep the toolip open on el click because the 'Create Dataset Metric'
				// becomes a part of this tooltip.
				closeOnAttachedToClick: !deviceType.touchOnly,
				extraClass: 'tooltip-and-details non-abbreviated-tooltip datasets-overview-non-abbreviated-tooltip'
			},
			details: (e, el) => {
				// We'll need to listen for a click on .create-dataset-metric since that option
				// is a part of this tooltip on mobile
				if (deviceType.touchOnly) {
					this.trigger('create-dataset-metric', this.getDatasetSeriesOptions(el))
				}
			}
		})
	},

	secure: function() {
		if (this.options.explore) { // #33313 when not on the explore tab, allow drilldowns
			// #19829 #15803 if no structure permission, can't create dataset series
			if (!permissions.anyStructure()) {
				this.$('.value').removeClass('value text-link-emphasized')
			}
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.nonAbbreviatedTooltip], 'remove')
	}

})

/**
 * Returns the filters that should be used when determining what to show in
 * the table header
 */
function headerContextFilters (filters) {
	return _.filter(filters, f => {
		// strip out permissions that are specific to just one column
		return _.isNil(f.aggregationId) &&
			// and also strip out filters that are for permissions
			_.isNil(f.permissionId)
			// #18614 and strip out filters that come from filter widgets, since
			// you can't go "back" on them from the table widget
			&& !f.filterWidget
			// Strip out filters from the drawer for the same reason we strip
			// out filter widget filters
			&& !f.drawerFilter
	})
}

/**
 * Returns context information for rendering the table header's back button and
 * filter text
 *
 * @param {ApiDatasetFilter} filters
 *
 * @param {Dataset} dataset
 *
 * @param {DatasetField|Object} underlyingField - the table's primary field
 *
 * @param {Map<Int,DatasetTree>} treeItemsById - dataset tree items that could
 * be in the filter, keyed by ID
 *
 * @param {Map<Int,ApiDatasetFilter[]>} filtersByLinkGroup - filters keyed by linkGroup
 *
 * @param {Int} [parentId] - parent dataset tree ID for the results. Comes from
 * TabularDatasetsQueryResult.parentId
 */
function headerFilterContext ({ filters, dataset, underlyingField, treeItemsById, filtersByLinkGroup, parentId }) {
	underlyingField = unwrapModel(underlyingField)

	let context = {}
	if (_.size(filters) == 1) {
		context.hasFilterText = true
		context.filterText = filterText(dataset, filters[0],
			underlyingField, treeItemsById)

		// #17676 if clicking back would remove an OR filter, don't show the back button
		context.back = true
		let filtersInLinkGroup = filtersByLinkGroup[filters[0].LinkGroup]
		if (_.size(filtersInLinkGroup) > 1 &&
			(!parentId ||
				// if the parent is the root
				parentId == underlyingField.treeId)) {
			context.back = false
		}
	} else {
		context.hasFilterText = false
	}

	return context
}

export default DatasetsResult
export { headerContextFilters }
export { headerFilterContext }

DatasetsResult.__filename__ = 'datasets/overview/datasets-result.js'
